import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import RiskBadge from "@/components/RiskBadge";
import { toast } from "sonner";
import { Newspaper, Sparkles, EyeOff, ListChecks, FileText, TrendingUp, TrendingDown, Activity, RefreshCw, Mail } from "lucide-react";

function Stat({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="border border-slate-200 bg-white rounded-md p-3">
      <div className={`font-display font-bold text-2xl ${tone}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">{label}</div>
    </div>
  );
}

function DeltaChip({ value, invertGoodness = false }) {
  const positive = value > 0;
  const negative = value < 0;
  const good = invertGoodness ? negative : positive;
  const tone = value === 0
    ? "bg-slate-100 text-slate-500 border-slate-200"
    : good
      ? "bg-red-50 text-red-700 border-red-200"
      : "bg-emerald-50 text-emerald-700 border-emerald-200";
  const sign = value > 0 ? "+" : "";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-mono px-1.5 py-0.5 rounded border ${tone}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : negative ? <TrendingDown className="h-3 w-3" /> : null}
      {sign}{value}
    </span>
  );
}

function TrendsPanel({ readOnly }) {
  const [trends, setTrends] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Debounce guard: kept in refs so recreating `load` doesn't reset either.
  // Cooldown timestamp is written BEFORE the request, so a failed refresh
  // still counts toward the debounce and can't be rage-retried.
  const REFRESH_COOLDOWN_MS = 4000;
  const lastRefreshAtRef = useRef(0);
  const busyRef = useRef(false);

  const load = useCallback(async (refresh = false) => {
    if (refresh) {
      if (busyRef.current) return;
      const now = Date.now();
      if (now - lastRefreshAtRef.current < REFRESH_COOLDOWN_MS) {
        toast.info("Trends were just refreshed. Try again in a moment.");
        return;
      }
      lastRefreshAtRef.current = now;
    }
    setLoading(!refresh);
    if (refresh) { busyRef.current = true; setBusy(true); }
    try {
      const { data } = await api.get("/digests/trends/weekly", { params: refresh ? { refresh: 1 } : {} });
      setTrends(data);
      if (refresh) toast.success("Trends recomputed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load weekly trends");
    } finally {
      setLoading(false);
      if (refresh) { busyRef.current = false; setBusy(false); }
    }
  }, []);

  useEffect(() => { load(false); }, [load]);

  if (loading) {
    return <div className="text-sm text-slate-500" data-testid="trends-loading">Loading weekly trends…</div>;
  }
  if (!trends) {
    return <div className="text-sm text-slate-500" data-testid="trends-empty">No trend data yet.</div>;
  }
  const d = trends.deltas || {};
  const tw = trends.this_week || {};
  const pw = trends.prev_week || {};
  return (
    <div className="space-y-6" data-testid="trends-panel">
      <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="trends-narrative">
        <div className="flex items-center justify-between mb-2">
          <div className="text-[11px] font-mono text-slate-400">
            Week ending {trends.week_end} · vs previous 7 days
          </div>
          {!readOnly && (
            <Button size="sm" variant="outline" onClick={() => load(true)} disabled={busy}
              data-testid="trends-refresh" className="gap-1.5 border-slate-300">
              <RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          )}
        </div>
        <p className="text-sm text-slate-800 leading-relaxed border-l-2 border-purple-500 pl-3">
          {trends.narrative}
        </p>
      </section>

      <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="trends-stats">
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-baseline justify-between">
            <div className="font-display font-bold text-2xl">{tw.new_ads ?? 0}</div>
            <DeltaChip value={d.new_ads ?? 0} />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
            New ads · was {pw.new_ads ?? 0}
          </div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-baseline justify-between">
            <div className="font-display font-bold text-2xl text-red-600">{tw.high_risk ?? 0}</div>
            <DeltaChip value={d.high_risk ?? 0} />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
            High risk · was {pw.high_risk ?? 0}
          </div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-baseline justify-between">
            <div className="font-display font-bold text-2xl text-amber-600">{tw.obfuscated ?? 0}</div>
            <DeltaChip value={d.obfuscated ?? 0} />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
            Obfuscation · was {pw.obfuscated ?? 0}
          </div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-3">
          <div className="flex items-baseline justify-between">
            <div className="font-display font-bold text-2xl">{tw.reused_phones ?? 0}</div>
            <DeltaChip value={d.reused_phones ?? 0} />
          </div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mt-0.5">
            Reused phones · was {pw.reused_phones ?? 0}
          </div>
        </div>
      </section>

      <div className="grid sm:grid-cols-2 gap-4">
        <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="trends-hot-networks">
          <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-red-600" /> Networks flaring up
          </h2>
          <ul className="space-y-2">
            {(trends.hot_networks || []).map((n) => (
              <li key={n.name} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{n.name}</span>
                <span className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                  {n.prev_week} → {n.this_week}
                  <DeltaChip value={n.delta} />
                </span>
              </li>
            ))}
            {(trends.hot_networks || []).length === 0 && <li className="text-xs text-slate-400">No network gained activity this week.</li>}
          </ul>
        </section>
        <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="trends-cooled-networks">
          <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-emerald-600" /> Networks cooling off
          </h2>
          <ul className="space-y-2">
            {(trends.cooled_networks || []).map((n) => (
              <li key={n.name} className="flex items-center justify-between gap-2 text-sm">
                <span className="truncate">{n.name}</span>
                <span className="flex items-center gap-2 text-[11px] text-slate-500 font-mono">
                  {n.prev_week} → {n.this_week}
                  <DeltaChip value={n.delta} invertGoodness />
                </span>
              </li>
            ))}
            {(trends.cooled_networks || []).length === 0 && <li className="text-xs text-slate-400">No network cooled this week.</li>}
          </ul>
        </section>
      </div>

      <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="trends-hot-localities">
        <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
          <Activity className="h-4 w-4 text-blue-600" /> Locality hot-spots
        </h2>
        <div className="flex flex-wrap gap-2">
          {(trends.hot_localities || []).map((l) => (
            <span key={l.name} className="text-xs font-mono px-2 py-1 rounded-md bg-red-50 border border-red-200 text-red-700 inline-flex items-center gap-1.5">
              {l.name}
              <span className="text-[10px] text-slate-500">{l.prev_week}→{l.this_week}</span>
              <DeltaChip value={l.delta} />
            </span>
          ))}
          {(trends.hot_localities || []).length === 0 && <span className="text-xs text-slate-400">No locality gained activity this week.</span>}
        </div>
      </section>

      <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="trends-watch">
        <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
          <ListChecks className="h-4 w-4 text-teal-600" /> Watch this week
        </h2>
        <ol className="space-y-2">
          {(trends.watch_this_week || []).map((p, i) => (
            <li key={`watch-${i}-${p}`} className="flex gap-3 text-sm">
              <span className="h-5 w-5 shrink-0 grid place-items-center rounded-full bg-slate-900 text-white text-[11px] font-mono">{i + 1}</span>
              {p}
            </li>
          ))}
        </ol>
      </section>
    </div>
  );
}

export default function Digest() {
  const { user } = useAuth();
  const readOnly = user?.role === "analyst";
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("daily");
  const [digests, setDigests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/digests");
      setDigests(data);
      setSelected((prev) => (prev ? data.find((d) => d.date === prev.date) || data[0] : data[0]) || null);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!isAdmin) return;
    api.get("/digests/email/status")
      .then(({ data }) => setEmailStatus(data))
      .catch(() => setEmailStatus({ enabled: false }));
  }, [isAdmin]);

  const regenerate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digests/generate");
      toast.success("Digest regenerated");
      setSelected(data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Generation failed");
    } finally { setBusy(false); }
  };

  const emailDigest = async () => {
    if (!selected) return;
    if (!emailStatus?.enabled) {
      toast.warning("Set BREVO_API_KEY on the backend to enable email sending.");
      return;
    }
    setEmailing(true);
    try {
      const { data } = await api.post(`/digests/${selected.date}/email`);
      if (data.sent) {
        toast.success(`Emailed to ${data.recipients} investigator${data.recipients === 1 ? "" : "s"}`);
      } else if (data.reason === "no_recipients") {
        toast.warning("No investigators with real email addresses on the roster.");
      } else {
        toast.error(`Email failed: ${data.reason || "unknown"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Email failed");
    } finally { setEmailing(false); }
  };

  const s = selected?.stats || {};

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-4xl" data-testid="digest-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-wider text-blue-700 flex items-center gap-1.5">
            <Newspaper className="h-4 w-4" /> Intelligence briefings
          </div>
          <h1 className="font-display font-bold tracking-tight text-2xl sm:text-3xl mt-1">
            {tab === "trends" ? "Weekly trends" : "Daily intelligence digest"}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {tab === "trends"
              ? "Compare the last 7 days to the previous week — see which networks flared up and which cooled off."
              : "Auto-generated every day from the last 24 hours of crawling, risk scoring and alerts."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {tab === "daily" && (
            <>
              <select data-testid="digest-date-select" value={selected?.date || ""}
                onChange={(e) => setSelected(digests.find((d) => d.date === e.target.value))}
                className="border border-slate-300 rounded-md text-sm px-2 py-2">
                {digests.map((d) => <option key={d.date} value={d.date}>{d.date}</option>)}
              </select>
              {!readOnly && (
                <Button data-testid="digest-generate-btn" onClick={regenerate} disabled={busy}
                  className="bg-slate-900 hover:bg-slate-800 gap-1.5">
                  <Sparkles className="h-4 w-4" /> {busy ? "Generating…" : "Regenerate"}
                </Button>
              )}
              {isAdmin && selected && (
                <Button
                  data-testid="digest-email-btn"
                  onClick={emailDigest}
                  disabled={emailing || !emailStatus?.enabled}
                  variant="outline"
                  title={emailStatus?.enabled ? `Send to roster from ${emailStatus.sender_email}` : "Set BREVO_API_KEY to enable"}
                  className="gap-1.5 border-slate-300"
                >
                  <Mail className="h-4 w-4" /> {emailing ? "Sending…" : "Email digest"}
                </Button>
              )}
            </>
          )}
        </div>
      </header>

      <div className="flex gap-1.5" data-testid="digest-tabs">
        {[
          { key: "daily", label: "Daily digest" },
          { key: "trends", label: "Weekly trends" },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            data-testid={`digest-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              tab === t.key ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:bg-slate-100"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "trends" ? (
        <TrendsPanel readOnly={readOnly} />
      ) : loading ? (
        <div className="text-sm text-slate-500" data-testid="digest-loading">Loading digest…</div>
      ) : !selected ? (
        <div className="border border-dashed border-slate-300 rounded-md p-10 text-center text-sm text-slate-500" data-testid="digest-empty">
          No digest yet. {readOnly ? "It will be generated automatically." : "Click Regenerate to create today's digest."}
        </div>
      ) : (
        <>
          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="digest-narrative">
            <div className="text-[11px] font-mono text-slate-400 mb-2">
              {selected.date} · covers {selected.period_start?.slice(0, 16).replace("T", " ")} → {selected.period_end?.slice(0, 16).replace("T", " ")} UTC
              · generated by {selected.generated_by === "scheduler" ? "scheduler" : "an investigator"}
            </div>
            <p className="text-sm text-slate-800 leading-relaxed border-l-2 border-blue-500 pl-3">{selected.narrative}</p>
          </section>

          <section className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="digest-stats">
            <Stat label="New ads" value={s.new_ads ?? 0} />
            <Stat label="High risk" value={s.high_risk ?? 0} tone="text-red-600" />
            <Stat label="Obfuscation caught" value={s.obfuscated_count ?? 0} tone="text-amber-600" />
            <Stat label="New alerts" value={s.new_alerts ?? 0} />
            <Stat label="Crawls done" value={s.crawl?.done ?? 0} />
            <Stat label="Duplicates skipped" value={s.crawl?.duplicate ?? 0} />
            <Stat label="Crawls failed" value={s.crawl?.failed ?? 0} tone={s.crawl?.failed ? "text-red-600" : "text-slate-900"} />
            <Stat label="New entities" value={s.new_entities ?? 0} />
          </section>

          <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="digest-priorities">
            <h2 className="font-display font-bold text-lg tracking-tight mb-3 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-teal-600" /> Today's priorities
            </h2>
            <ol className="space-y-2">
              {(selected.priorities || []).map((p, i) => (
                <li key={`pri-${i}-${p}`} className="flex gap-3 text-sm">
                  <span className="h-5 w-5 shrink-0 grid place-items-center rounded-full bg-slate-900 text-white text-[11px] font-mono">{i + 1}</span>
                  {p}
                </li>
              ))}
            </ol>
          </section>

          <div className="grid sm:grid-cols-2 gap-4">
            <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="digest-top-ads">
              <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
                <FileText className="h-4 w-4 text-blue-600" /> Highest-risk new ads
              </h2>
              <ul className="space-y-2">
                {(selected.top_ads || []).map((a) => (
                  <li key={a.id}>
                    <Link to={`/ads/${a.id}`} data-testid={`digest-ad-${a.id}`} className="flex items-center justify-between gap-2 hover:bg-slate-50 rounded-md px-1.5 py-1 -mx-1.5">
                      <div className="min-w-0">
                        <div className="text-sm truncate">{a.title}</div>
                        <div className="text-[11px] text-slate-400 font-mono truncate">{(a.top_reasons || []).join(" · ")}</div>
                      </div>
                      <RiskBadge level={a.level} score={a.score} />
                    </Link>
                  </li>
                ))}
                {(selected.top_ads || []).length === 0 && <li className="text-xs text-slate-400">No new ads in this period.</li>}
              </ul>
            </section>

            <section className="border border-slate-200 bg-white rounded-md p-5" data-testid="digest-obfuscation">
              <h2 className="font-display font-bold text-base tracking-tight mb-3 flex items-center gap-2">
                <EyeOff className="h-4 w-4 text-amber-600" /> Obfuscation attempts caught
              </h2>
              <ul className="space-y-2">
                {(selected.obfuscation_ads || []).map((a) => (
                  <li key={a.id}>
                    <Link to={`/ads/${a.id}`} className="block hover:bg-slate-50 rounded-md px-1.5 py-1 -mx-1.5">
                      <div className="text-sm truncate">{a.title}</div>
                      <div className="flex gap-1 mt-0.5">
                        {(a.techniques || []).map((t) => (
                          <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-amber-800">{t.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    </Link>
                  </li>
                ))}
                {(selected.obfuscation_ads || []).length === 0 && <li className="text-xs text-slate-400">None detected in this period.</li>}
              </ul>
            </section>
          </div>
        </>
      )}
    </div>
  );
}
